// Carte enrichie de l'application terrain (MapLibre, style de la charte construit par @cc/ui) :
// objet ciblé, points de collecte numérotés, position de l'agent si elle est autorisée, thème suivi
// en direct. Hors connexion, seules les tuiles en cache s'affichent et la carte le dit ; sans WebGL,
// la liste des points reste lisible. Le fond est celui de CarteTerritoire (OpenFreeMap, sans clé).
import { FOND_OPENFREEMAP, construireStyle, lireJetons, chargerMapLibre, type Territoire } from '@cc/ui';
import type { Map as CarteMapLibre, Marker } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { distanceLisible, distanceMetres, suivrePosition, type EtatPosition } from '../../terrain/position';
import { useEnLigne } from '../../terrain/useTerrain';
import styles from './terrain.module.css';

/** Position d'un objet emporté (contrat : longitude et latitude). */
type PositionObjet = { longitude: number; latitude: number };

export interface PointCarte {
  id: string;
  numero: number;
  libelle: string;
  position: PositionObjet;
}

export interface ProprietesCarteTerrain {
  titre: string;
  cible?: { libelle: string; position: PositionObjet } | null;
  points?: PointCarte[];
  /** Suivre la position de l'agent pendant l'affichage (si l'appareil l'autorise). */
  suivre?: boolean;
  hauteur?: number;
}

/** Zone sans limite dessinée : ni masque ni contour, le fond seul. */
const ZONE: Territoire = { id: 'zone-travail', niveau: 'commune', libelle: 'Zone de travail', geometrie: null, indicatif: false, source: null };

type Etat = 'chargement' | 'pret' | 'webgl_absent' | 'erreur';

function webglDisponible(): boolean {
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2'));
  } catch {
    return false;
  }
}

function marqueur(classe: string, texte: string, libelle: string): HTMLElement {
  const e = document.createElement('span');
  e.className = classe;
  e.textContent = texte;
  e.setAttribute('role', 'img');
  e.setAttribute('aria-label', libelle);
  return e;
}

export function CarteTerrain({ titre, cible = null, points = [], suivre = false, hauteur = 320 }: ProprietesCarteTerrain) {
  const conteneur = useRef<HTMLDivElement>(null);
  const carte = useRef<CarteMapLibre | null>(null);
  const module = useRef<typeof import('maplibre-gl') | null>(null);
  const poses = useRef<Marker[]>([]);
  const marqueurAgent = useRef<Marker | null>(null);
  const [etat, setEtat] = useState<Etat>('chargement');
  const [fondOk, setFondOk] = useState<boolean | null>(null);
  const [position, setPosition] = useState<EtatPosition>({ position: null, autorisation: 'inconnue', erreur: null });
  const enLigne = useEnLigne();
  const positions = [...(cible ? [cible.position] : []), ...points.map((p) => p.position)];
  const signature = JSON.stringify(positions.map((p) => [p.longitude, p.latitude]));

  useEffect(() => {
    if (!conteneur.current) return;
    if (!webglDisponible()) {
      setEtat('webgl_absent');
      return;
    }
    let annule = false;
    let observateur: MutationObserver | null = null;
    const media = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    const appliquerTheme = () => carte.current?.setStyle(construireStyle(lireJetons(), FOND_OPENFREEMAP, { echelle: ZONE }), { diff: true });
    void (async () => {
      try {
        const maplibre = await chargerMapLibre();
        await import('maplibre-gl/dist/maplibre-gl.css');
        if (annule || !conteneur.current) return;
        module.current = maplibre;
        const premiere = positions[0];
        const instance = new maplibre.Map({
          zoomLevelsToOverscale: undefined, // Conserver le rendu de MapLibre 5.
          container: conteneur.current,
          style: construireStyle(lireJetons(), FOND_OPENFREEMAP, { echelle: ZONE }),
          center: premiere ? [premiere.longitude, premiere.latitude] : [9.45, 0.39],
          zoom: 15,
          attributionControl: { compact: true },
          locale: { 'NavigationControl.ZoomIn': 'Zoomer', 'NavigationControl.ZoomOut': 'Dézoomer', 'AttributionControl.ToggleAttribution': 'Afficher les sources' },
        });
        instance.addControl(new maplibre.NavigationControl({ showCompass: false }), 'bottom-right');
        instance.on('load', () => !annule && setEtat('pret'));
        instance.on('error', (e: { sourceId?: string; error?: unknown }) => {
          if (e.sourceId === 'fond' || String((e.error as { message?: string } | undefined)?.message ?? '').includes('fond')) setFondOk(false);
        });
        instance.on('sourcedata', (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
          if (e.sourceId === 'fond' && e.isSourceLoaded) setFondOk(true);
        });
        carte.current = instance;
        observateur = new MutationObserver(appliquerTheme);
        observateur.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        media?.addEventListener?.('change', appliquerTheme);
      } catch {
        if (!annule) setEtat('erreur');
      }
    })();
    return () => {
      annule = true;
      observateur?.disconnect();
      media?.removeEventListener?.('change', appliquerTheme);
      for (const m of poses.current) m.remove();
      poses.current = [];
      marqueurAgent.current?.remove();
      marqueurAgent.current = null;
      carte.current?.remove();
      carte.current = null;
    };
    // La carte est créée une fois ; les positions sont appliquées par l'effet suivant.
  }, []);

  // Objet ciblé et points : marqueurs HTML et recadrage sur l'ensemble.
  useEffect(() => {
    const c = carte.current;
    const maplibre = module.current;
    if (!c || !maplibre || etat !== 'pret') return;
    for (const m of poses.current) m.remove();
    poses.current = [];
    if (cible) poses.current.push(new maplibre.Marker({ element: marqueur(styles.marqueurCible as string, '●', `Objet de la mission : ${cible.libelle}`) }).setLngLat([cible.position.longitude, cible.position.latitude]).addTo(c));
    for (const p of points) poses.current.push(new maplibre.Marker({ element: marqueur(styles.marqueurPoint as string, String(p.numero).padStart(2, '0'), `Point ${p.numero} : ${p.libelle}`) }).setLngLat([p.position.longitude, p.position.latitude]).addTo(c));
    if (positions.length > 1) {
      const lons = positions.map((p) => p.longitude);
      const lats = positions.map((p) => p.latitude);
      c.fitBounds([Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)], { padding: 48, maxZoom: 16, duration: 0 });
    }
    // La signature résume les positions ; cible et points sont des objets recréés à chaque rendu.
  }, [signature, etat]);

  // Position de l'agent.
  useEffect(() => {
    if (!suivre) return;
    return suivrePosition(setPosition);
  }, [suivre]);
  useEffect(() => {
    const c = carte.current;
    const maplibre = module.current;
    if (!c || !maplibre || etat !== 'pret') return;
    const p = position.position;
    if (!p) return;
    if (!marqueurAgent.current) marqueurAgent.current = new maplibre.Marker({ element: marqueur(styles.marqueurAgent as string, '', 'Ma position') }).setLngLat([p.longitude, p.latitude]).addTo(c);
    else marqueurAgent.current.setLngLat([p.longitude, p.latitude]);
  }, [position, etat]);

  const distance = position.position && cible ? distanceMetres(position.position, cible.position) : null;
  const message = etat === 'webgl_absent' ? 'Affichage cartographique indisponible sur cet appareil (WebGL absent) : les points sont listés ci-dessous.' : etat === 'erreur' ? "La carte n'a pas pu être chargée." : !enLigne ? "Hors connexion : seules les tuiles déjà en cache sur cet appareil s'affichent." : fondOk === false ? 'Fond de carte injoignable : les repères restent affichés.' : null;

  return (
    <figure className={styles.pile} data-etat={etat} data-fond={fondOk === null ? 'inconnu' : fondOk ? 'ok' : 'indisponible'}>
      {/* Région nommée plutôt qu'image : la carte contient des boutons (zoom), qu'un rôle image rendrait inaccessibles. */}
      <div className={styles.cadreCarte} style={{ height: hauteur }} role="region" aria-label={`${titre}${cible ? ` : ${cible.libelle}` : ''}${points.length ? `, ${points.length} points` : ''}`}>
        <div ref={conteneur} className={styles.conteneurCarte} />
        {message ? <p className={styles.messageCarte}>{message}</p> : null}
      </div>
      <ul className={styles.legendeCarte} aria-label="Légende">
        {cible ? (
          <li>
            <span className={styles.marqueurCible} aria-hidden style={{ width: 14, height: 14, fontSize: 0 }} /> Objet de la mission
          </li>
        ) : null}
        {points.length ? (
          <li>
            <span className={styles.marqueurPoint} aria-hidden style={{ width: 14, height: 14, fontSize: 0 }} /> Point de collecte
          </li>
        ) : null}
        {suivre ? (
          <li>
            <span className={styles.marqueurAgent} aria-hidden style={{ width: 14, height: 14 }} /> Ma position{position.position ? ` (précision ${position.position.precision_m ?? '?'} m${distance !== null ? `, à ${distanceLisible(distance)} de l'objet` : ''})` : position.erreur ? ` : ${position.erreur}` : ' : en attente'}
          </li>
        ) : null}
      </ul>
      <ul className={etat === 'webgl_absent' ? styles.liste : 'cc-masque'} aria-label="Repères de la carte">
        {cible ? <li>Objet de la mission : {cible.libelle}</li> : null}
        {points.map((p) => (
          <li key={p.id}>
            Point {p.numero} : {p.libelle}
          </li>
        ))}
      </ul>
      <figcaption className={styles.attribution}>Fond : {FOND_OPENFREEMAP.libelle}.</figcaption>
    </figure>
  );
}
